// The app's own sign-in landing page.
//
// Sign-in is deliberately NOT automatic: arriving at app.shelter-stone.com and
// being thrown straight at login.microsoftonline.com is disconcerting (you can't
// tell what you've landed on, or that you're meant to be there). So we render our
// own branded page first and only redirect to Microsoft when the user asks.
//
// This component is presentational — AuthGate owns the MSAL interaction and
// passes the handler/state in.
import { Box, Paper, Typography, Button, Alert, CircularProgress } from "@mui/material";

const LOGO = process.env.PUBLIC_URL + "/ShelterStoneLogo.png";

// The Microsoft four-square mark, inline so the button needs no extra asset.
// Microsoft's brand guidance asks for the logo alongside "Sign in with Microsoft".
function MicrosoftIcon() {
  return (
    <Box component="svg" viewBox="0 0 23 23" aria-hidden="true" sx={{ width: 18, height: 18 }}>
      <rect x="1" y="1" width="10" height="10" fill="#f25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7fba00" />
      <rect x="1" y="12" width="10" height="10" fill="#00a4ef" />
      <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
    </Box>
  );
}

/** Shared shell for every pre-app screen (sign in, signing in, access denied) so
 *  they read as one page rather than three. */
export function AuthCard({ children }) {
  const year = new Date().getFullYear();
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        p: 2,
        // A hint of the app's moss/stone palette so the page feels like the app.
        background: (t) =>
          `linear-gradient(160deg, ${t.palette.background.default} 0%, ${t.palette.primary.light}22 100%)`,
      }}
    >
      <Paper
        elevation={2}
        sx={{
          width: "100%",
          maxWidth: 440,
          p: { xs: 3, sm: 5 },
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box
          component="img"
          src={LOGO}
          alt="Shelter Stone"
          sx={{ width: "100%", maxWidth: 280 }}
        />
        {children}
      </Paper>
      <Typography variant="caption" color="text.secondary">
        © {year} Shelter Stone
      </Typography>
    </Box>
  );
}

export default function SignIn({ onSignIn, busy, error }) {
  return (
    <AuthCard>
      <Typography color="text.secondary">
        Incident, event, inventory and personnel management for the Shelter Stone team.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ width: "100%", textAlign: "left" }}>
          {error.errorMessage || error.message || String(error)}
        </Alert>
      )}
      <Button
        variant="contained"
        size="large"
        fullWidth
        disabled={busy}
        onClick={onSignIn}
        startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <MicrosoftIcon />}
        sx={{ mt: 1 }}
      >
        {busy ? "Taking you to Microsoft…" : "Sign in with Microsoft"}
      </Button>
      <Typography variant="caption" color="text.secondary">
        Use the Microsoft account you were invited with. If you can't get in, ask a
        Shelter Stone administrator for access.
      </Typography>
    </AuthCard>
  );
}
